import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconButton, Tooltip } from "@mui/material";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";

import { useEffectivePermissions, toSet, canEmitirBPe } from "../../hooks/useEffectivePermissions";
import { useWorkspaceSegment } from "../../hooks/useWorkspaceSegment";
import { useWorkspaceConfig } from "../../context/WorkspaceConfigContext";
import { WorkspaceSegments } from "../../constants/workspaceSegments";
import {
  passagensByKeyFromConfigurationsResponse,
  hasAnyPassagensPartnerConfigured,
} from "../../constants/passagensConfigHelpers";
import { formatCpfDisplay } from "../../api/bpePassengerClients";
import BPeWizard from "./BPeWizard";

/** Lê dicas opcionais nas tags: bpe_origem:... e bpe_destino:... */
function parseBpeTags(tags) {
  const out = { origem: "", destino: "" };
  if (!tags || typeof tags !== "string") return out;
  for (const raw of tags.split(/[,\n;]+/)) {
    const p = raw.trim();
    const low = p.toLowerCase();
    if (low.startsWith("bpe_origem:")) out.origem = p.slice(p.indexOf(":") + 1).trim();
    if (low.startsWith("bpe_destino:")) out.destino = p.slice(p.indexOf(":") + 1).trim();
  }
  return out;
}

/**
 * Atalho de emissão de BPe exibido no cabeçalho da conversa, ao lado dos
 * ícones de abrir em popup e detalhes. Segue as mesmas regras de exibição do
 * card de Passagens (segmento rodoviário + permissão bpe.view + integração ERP).
 */
const BPeQuickSellButton = ({ selectedConversation, onSuccess }) => {
  const { t } = useTranslation("chat");
  const [wizardOpen, setWizardOpen] = useState(false);

  const { data: permissionsData } = useEffectivePermissions();
  const { segment, isLoading: isLoadingSegment } = useWorkspaceSegment();
  const { items: wsConfigItems, isLoading: isLoadingWsConfig } = useWorkspaceConfig();
  const scopesSet = toSet(permissionsData?.scopes);

  const hasPassagensIntegration = useMemo(
    () =>
      hasAnyPassagensPartnerConfigured(
        passagensByKeyFromConfigurationsResponse({ data: { items: wsConfigItems || [] } })
      ),
    [wsConfigItems]
  );

  const conversationId = selectedConversation?.id ?? selectedConversation?.id_lead;

  const canShowBPe =
    !isLoadingSegment &&
    !isLoadingWsConfig &&
    segment === WorkspaceSegments.PassengerTransport &&
    canEmitirBPe(scopesSet) &&
    hasPassagensIntegration;

  const { origem: hintOrigem, destino: hintDestino } = useMemo(
    () => parseBpeTags(selectedConversation?.tags),
    [selectedConversation?.tags]
  );

  const clientPrefill = useMemo(
    () => ({
      clientId: selectedConversation?.clientId ? Number(selectedConversation.clientId) : null,
      projectId: selectedConversation?.projectId ?? selectedConversation?.id_projeto ?? null,
      nome: selectedConversation?.nome?.trim() || "",
      email: selectedConversation?.email?.trim() || "",
      celular: selectedConversation?.celular?.trim() || "",
      documento: formatCpfDisplay(selectedConversation?.cpf) || "",
    }),
    [
      selectedConversation?.clientId,
      selectedConversation?.projectId,
      selectedConversation?.id_projeto,
      selectedConversation?.nome,
      selectedConversation?.email,
      selectedConversation?.celular,
      selectedConversation?.cpf,
    ]
  );

  if (!canShowBPe || !conversationId) return null;

  return (
    <>
      <Tooltip title={t("passagensBpe.sellTicket")}>
        <IconButton
          onClick={() => setWizardOpen(true)}
          aria-label={t("passagensBpe.sellTicket")}
          sx={{ color: "primary.main" }}
        >
          <ConfirmationNumberIcon />
        </IconButton>
      </Tooltip>

      <BPeWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        conversationId={conversationId}
        onSuccess={onSuccess}
        initialOrigem={hintOrigem}
        initialDestino={hintDestino}
        clientPrefill={clientPrefill}
        channelInfo={{
          channelId: selectedConversation?.channelId ?? null,
          channelType: selectedConversation?.channelType ?? null,
          projectId: clientPrefill?.projectId ?? null,
          phone: clientPrefill?.celular ?? "",
        }}
      />
    </>
  );
};

export default BPeQuickSellButton;
